import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, TextField, Typography, Paper, InputAdornment, IconButton } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { exchangeImpersonationTokenApi, loginApi } from "../api";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import fbrLogo from "../assets/fbricon.png"; 
import { useData } from "../ContextApi/ContextProvider";

const Login = () => {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [isImpersonating, setIsImpersonating] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const impersonationToken = searchParams.get("impersonation_token");
  const { setGlobalData, setInvoices, setUser, setLoader } = useData();
  const loginRequestId = useRef(0);
  const clearUserData = useCallback(() => {
    setGlobalData(null);
    setInvoices([]);
    setUser(null);
    setLoader(false);
  }, [setGlobalData, setInvoices, setUser, setLoader]);

  useEffect(() => () => {
    loginRequestId.current += 1;
  }, []);

  useEffect(() => {
    if (impersonationToken) {
      return;
    }

    const token = localStorage.getItem("jwt_token");
    if (token) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate, impersonationToken]);

  useEffect(() => {
    if (!impersonationToken) {
      return;
    }

    const requestId = ++loginRequestId.current;
    const loginAsClient = async () => {
      setIsImpersonating(true);
      setError("");

      try {
        localStorage.removeItem("jwt_token");
        localStorage.removeItem("details");
        clearUserData();
        const res = await exchangeImpersonationTokenApi(impersonationToken);
        if (requestId !== loginRequestId.current) return;
        localStorage.setItem("jwt_token", res.response.token);
        localStorage.setItem("details", JSON.stringify(res.response));
        setGlobalData(res.response.detail || null);
        navigate("/dashboard", { replace: true });
      } catch (err) {
        if (requestId !== loginRequestId.current) return;
        setError(err?.message || "Failed to login as client.");
      } finally {
        if (requestId === loginRequestId.current) setIsImpersonating(false);
      }
    };

    loginAsClient();
    return () => {
      if (requestId === loginRequestId.current) loginRequestId.current += 1;
    };
  }, [navigate, impersonationToken, clearUserData, setGlobalData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const requestId = ++loginRequestId.current;
    setError("");
    try {
      const res = await loginApi(email, pass);
      if (requestId !== loginRequestId.current) return;
      if(res.message==="Invalid email or password")
      {
        setError("Invalid credentials");
      }
      else{
        clearUserData();
        localStorage.setItem("jwt_token", res.response.token);
        localStorage.setItem("details", JSON.stringify(res.response));
        setGlobalData(res.response.detail || null);
        navigate("/dashboard");
      }
     
    } catch {
      if (requestId !== loginRequestId.current) return;
      setError("Invalid credentials");
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        background: "radial-gradient(ellipse at top left, #c3c6ff 10%, #9f9eea 70%, #1b1431 100%)",
      }}
    >
      <Paper
        elevation={12}
        sx={{
          p: { xs: 3, sm: 5 },
          minWidth: { xs: 320, sm: 400 },
          maxWidth: 430,
          borderRadius: 6,
          backdropFilter: "blur(7px)",
          background: "rgba(255,255,255,0.13)",
          boxShadow: "0 6px 32px 2px #5f5b96, 0 1.5px 4px #fff5",
          border: "1px solid #e0e0ff55",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* 3D logo circle */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          mb={2}
        >
          <Box
            sx={{
              p: 1.2,
              borderRadius: "50%",
              background: "linear-gradient(145deg, #b9aefb 40%, #8176cb 100%)",
              boxShadow: "0 2px 12px #887cc5, 0 0.5px 2px #fff9",
              display: "inline-flex",
            }}
          >
            <img
              src={fbrLogo}
              alt="KonnectFBR Logo"
              style={{
                width: 56,
                height: 56,
                objectFit: "contain",
                borderRadius: "50%",
                boxShadow: "0 2px 8px #8369df77, 0 0.5px 2px #fff7",
                background: "#e8eafdcc",
              }}
            />
          </Box>
        </Box>
        <Typography
          variant="h5"
          align="center"
          fontWeight={700}
          mb={0.5}
          sx={{ color: "#312a66", textShadow: "0 2px 16px #a79ff9" }}
        >
          Konnect <span style={{ color: "#6c5ad7" }}>FBR</span>
        </Typography>
        <Typography
          align="center"
          mb={3}
          fontSize={17}
          fontWeight={400}
          sx={{ color: "#6c5ad7" }}
        >
          Secure Invoice Portal
        </Typography>
        {isImpersonating && (
          <Typography align="center" mb={2} fontSize={14} sx={{ color: "#312a66" }}>
            Signing in as client, please wait...
          </Typography>
        )}
        <form onSubmit={handleLogin} autoComplete="off">
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoFocus
            required
            disabled={isImpersonating}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{ background: "#f8f9ffcc", borderRadius: 2, mb: 1 }}
          />
          <TextField
            label="Password"
            type={showPass ? "text" : "password"}
            fullWidth
            margin="normal"
            value={pass}
            onChange={e => setPass(e.target.value)}
            required
            disabled={isImpersonating}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPass(x => !x)}
                    tabIndex={-1}
                  >
                    {showPass ? (
                      <span role="img" aria-label="Hide">
                        👁️‍🗨️
                      </span>
                    ) : (
                      <span role="img" aria-label="Show">
                        👁️
                      </span>
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ background: "#f8f9ffcc", borderRadius: 2 }}
          />
          {error && (
            <Typography color="error" fontSize={14} align="center" mt={1}>
              {error}
            </Typography>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isImpersonating}
            sx={{
              mt: 3,
              borderRadius: 3,
              boxShadow: "0 2px 12px #7c66d7, 0 1.5px 4px #fff8",
              fontWeight: 700,
              letterSpacing: 1,
              background: "linear-gradient(90deg,#6c5ad7 60%, #46359f 100%)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(90deg,#4735a0 60%, #6c5ad7 100%)"
              }
            }}
          >
            Login
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default Login;
